(() => {
  if (window.ArchCart) return;

  const nativeFetch = window.fetch.bind(window);
  const mutationListeners = new Set();
  const beforeCheckoutListeners = new Set();
  const cartMutationPattern = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  let operationQueue = Promise.resolve();
  let checkoutSyncInFlight = false;
  const CHECKOUT_GUARD_MS = 3000;

  const requestUrl = (input) => {
    if (typeof input === 'string') return input;
    return input && typeof input.url === 'string' ? input.url : '';
  };

  const enqueueMutation = (task) => {
    const run = operationQueue.then(task, task);
    operationQueue = run.catch(() => undefined);
    return run;
  };

  window.fetch = function archCartFetch(input, init) {
    const url = requestUrl(input);
    const result = nativeFetch(input, init);

    if (cartMutationPattern.test(url)) {
      result
        .then((response) => {
          if (!response.ok) return;
          mutationListeners.forEach((listener) => {
            try {
              listener({ url, response });
            } catch (error) {
              console.error('[archiduchesse] Listener panier en erreur :', error);
            }
          });
        })
        .catch(() => {});
    }

    return result;
  };

  const onCartMutation = (listener) => {
    if (typeof listener !== 'function') return () => {};
    mutationListeners.add(listener);
    return () => mutationListeners.delete(listener);
  };

  const onBeforeCheckout = (listener) => {
    if (typeof listener !== 'function') return () => {};
    beforeCheckoutListeners.add(listener);
    return () => beforeCheckoutListeners.delete(listener);
  };

  document.addEventListener('click', (event) => {
    const target = event.target && typeof event.target.closest === 'function'
      ? event.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [formaction*="/checkout"]')
      : null;
    if (!target || beforeCheckoutListeners.size === 0 || window.__archCartCheckoutReady) return;

    event.preventDefault();
    event.stopPropagation();
    if (checkoutSyncInFlight) return;
    checkoutSyncInFlight = true;

    const guard = new Promise((resolve) => window.setTimeout(resolve, CHECKOUT_GUARD_MS));
    const sync = Array.from(beforeCheckoutListeners).reduce(
      (promise, listener) => promise.then(() => Promise.resolve(listener()).catch(() => null)),
      Promise.resolve()
    );

    Promise.race([sync, guard]).finally(() => {
      checkoutSyncInFlight = false;
      window.__archCartCheckoutReady = true;
      if (target.tagName === 'A' && target.href) {
        window.location.href = target.href;
      } else if (typeof target.click === 'function') {
        target.click();
      } else if (target.form) {
        target.form.submit();
      }
      window.setTimeout(() => {
        window.__archCartCheckoutReady = false;
      }, CHECKOUT_GUARD_MS);
    });
  }, true);

  const cartRequest = (path, payload, label = 'panier') => nativeFetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(payload)
  }).then((response) => {
    if (!response.ok) {
      throw new Error('Echec ' + path + ' (' + response.status + ') pour ' + label);
    }
    return response;
  });

  const fetchCart = () => nativeFetch('/cart.js', {
    headers: { 'X-Requested-With': 'XMLHttpRequest' }
  }).then((response) => {
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  });

  const refreshThemeCart = (source) => {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source });
        return;
      }
    } catch (error) {
      /* pubsub indisponible : repli direct ci-dessous */
    }

    document.querySelectorAll('cart-items, cart-drawer-items').forEach((element) => {
      if (typeof element.onCartUpdate === 'function') element.onCartUpdate();
    });
  };

  const getHiddenLogisticsSkus = () => {
    try {
      const element = document.getElementById('arch-hidden-logistics');
      if (!element) return new Set();
      const parsed = JSON.parse(element.textContent || '{}');
      if (!parsed || !Array.isArray(parsed.skus)) return new Set();
      return new Set(parsed.skus.map((sku) => String(sku)));
    } catch (error) {
      return new Set();
    }
  };

  const createReconciler = ({
    name,
    source,
    task,
    defaultDelay = 450,
    maxFailures = 5
  }) => {
    let timer = null;
    let inFlight = false;
    let pending = false;
    let retryPending = false;
    let consecutiveFailures = 0;
    let currentRun = Promise.resolve(null);

    const schedule = (delay = defaultDelay, rearm = false) => {
      if (rearm && consecutiveFailures >= maxFailures) consecutiveFailures = 0;
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        run();
      }, typeof delay === 'number' ? delay : defaultDelay);
    };

    const run = () => {
      if (inFlight) {
        pending = true;
        return currentRun;
      }

      inFlight = true;
      currentRun = enqueueMutation(() => Promise.resolve().then(task))
        .then((response) => {
          consecutiveFailures = 0;
          if (response) refreshThemeCart(source);
          return response;
        })
        .catch((error) => {
          consecutiveFailures += 1;
          if (consecutiveFailures >= maxFailures) {
            console.warn(
              '[archiduchesse] Réconciliation ' + name + ' abandonnée après '
                + consecutiveFailures
                + ' échecs consécutifs — la prochaine interaction panier réessaiera :',
              error
            );
          } else {
            console.error('[archiduchesse] Réconciliation ' + name + ' échouée :', error);
            retryPending = true;
          }
          return null;
        })
        .finally(() => {
          inFlight = false;
          if (pending) {
            pending = false;
            retryPending = false;
            schedule(250);
          } else if (retryPending) {
            retryPending = false;
            schedule(Math.min(30000, 250 * Math.pow(2, consecutiveFailures)));
          }
        });

      return currentRun;
    };

    const trigger = (delay = defaultDelay) => schedule(delay, true);

    const flush = () => {
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
      return currentRun.catch(() => null).then(() => {
        if (timer) {
          window.clearTimeout(timer);
          timer = null;
        }
        return run();
      });
    };

    return { flush, run, trigger };
  };

  window.ArchCart = Object.freeze({
    cartRequest,
    createReconciler,
    enqueueMutation,
    fetchCart,
    getHiddenLogisticsSkus,
    onBeforeCheckout,
    onCartMutation,
    refreshThemeCart
  });
})();
